<div class="modal-header">
    <h4 class="modal-title">
        @if (isAdding) {
            New Disciplinary Action
        } @else {
            Edit Disciplinary Action
        }
    </h4>
</div>

@if (isAdding) {
    <div class="wizard-steps">
        <div class="wizard-step" [class.active]="currentStep === 1" [class.completed]="currentStep > 1">
            <div class="wizard-step-circle">
                @if (currentStep > 1) {
                    <fa-icon [icon]="checkIcon"></fa-icon>
                } @else {
                    1
                }
            </div>
            <div class="wizard-step-label">Record</div>
        </div>
        <div class="wizard-step-connector" [class.completed]="currentStep > 1"></div>
        <div class="wizard-step" [class.active]="currentStep === 2" [class.completed]="currentStep > 2">
            <div class="wizard-step-circle">
                @if (currentStep > 2) {
                    <fa-icon [icon]="checkIcon"></fa-icon>
                } @else {
                    2
                }
            </div>
            <div class="wizard-step-label">Review</div>
        </div>
        <div class="wizard-step-connector" [class.completed]="currentStep > 2"></div>
        <div class="wizard-step" [class.active]="currentStep === 3" [class.completed]="false">
            <div class="wizard-step-circle">3</div>
            <div class="wizard-step-label">Confirm</div>
        </div>
    </div>
}

@if (isAdding && currentStep === 1) {
    <!-- STEP 1: Dictation Prompt + Recording -->
    <div class="modal-body">
        <div class="step-card">
            <div class="step-card-header">
                <div class="step-card-header-icon icon-record">
                    <fa-icon [icon]="micIcon"></fa-icon>
                </div>
                <div class="step-card-header-text">
                    <div class="step-card-title">Record Dictation</div>
                    <div class="step-card-subtitle">Describe the disciplinary incident in your own words</div>
                </div>
            </div>

            <div class="dictation-tips">
                <div class="dictation-tips-title">Tips for a complete record</div>
                <ul>
                    <li>Clearly state the <strong>employee's full name</strong></li>
                    <li>A <strong>description of the incident</strong> and <strong>when it occurred</strong></li>
                    <li>What <strong>disciplinary action</strong> was taken</li>
                    <li>Whether a <strong>warning notice</strong> was issued</li>
                    <li>The <strong>level of progressive discipline</strong> (verbal, written, final, etc.)</li>
                    <li>Any <strong>follow-up date</strong> or <strong>employee or supervisor comments</strong></li>
                </ul>
            </div>

            <span class="form-label fw-semibold d-block" style="font-size: 13px">Transcription</span>
            <app-voice-text-field
                name="disciplinaryWizardTranscription"
                [(ngModel)]="finalText"
                [rows]="10"
                [autoStart]="voiceAutoStart"
                placeholder="Speak or type your notes here — transcription builds as you dictate."
            ></app-voice-text-field>
        </div>
    </div>
    <div class="modal-footer">
        <div class="wizard-footer">
            <div></div>
            <div class="wizard-footer-end">
                <button class="btn btn-secondary" (click)="cancel()">Cancel</button>
                <button class="btn btn-primary" (click)="goToStep(2)" [disabled]="!finalText">
                    Next: Review <fa-icon [icon]="arrowRightIcon" class="ms-1"></fa-icon>
                </button>
            </div>
        </div>
    </div>
}

@if (isAdding && currentStep === 2) {
    <!-- STEP 2: Review + AI Extraction (combined) -->
    <div class="modal-body">
        <div class="step-card">
            @if (!isExtracting) {
                <div class="step-card-header">
                    <div class="step-card-header-icon icon-edit">
                        <fa-icon [icon]="editIcon"></fa-icon>
                    </div>
                    <div class="step-card-header-text">
                        <div class="step-card-title">Review Transcription</div>
                        <div class="step-card-subtitle">Edit the text if needed, then send to AI for field extraction</div>
                    </div>
                </div>

                <textarea
                    class="form-control"
                    [(ngModel)]="finalText"
                    name="transcriptionEdit"
                    id="transcriptionEdit"
                    rows="10"
                    placeholder="Edit the transcription text..."
                    style="border-radius: 10px; font-size: 14px"
                ></textarea>

                @if (extractionError) {
                    <div class="alert alert-danger mt-3 mb-0"><strong>Extraction failed:</strong> {{ extractionError }}</div>
                }
            } @else {
                <div class="ai-loading-container">
                    <div
                        class="step-card-header-icon icon-ai"
                        style="width: 56px; height: 56px; min-width: 56px; border-radius: 50%; font-size: 24px"
                    >
                        <fa-icon [icon]="faSpinner" [animation]="'spin'"></fa-icon>
                    </div>
                    <div class="ai-loading-text">Extracting fields...</div>
                    <div class="ai-loading-subtext">This usually takes a few seconds</div>
                </div>
            }
        </div>
    </div>
    <div class="modal-footer">
        <div class="wizard-footer">
            <button class="btn btn-outline-secondary" (click)="goToStep(1)" [disabled]="isExtracting">
                <fa-icon [icon]="arrowLeftIcon" class="me-1"></fa-icon> Back
            </button>
            <div class="wizard-footer-end">
                <button class="btn btn-secondary" (click)="cancel()" [disabled]="isExtracting">Cancel</button>
                <button class="btn btn-primary" (click)="sendToAi()" [disabled]="!finalText || isExtracting">
                    @if (isExtracting) {
                        <fa-icon [icon]="faSpinner" [animation]="'spin'" class="me-1"></fa-icon> Extracting...
                    } @else if (extractionError) {
                        <fa-icon [icon]="wandIcon" class="me-1"></fa-icon> Retry
                    } @else {
                        <fa-icon [icon]="wandIcon" class="me-1"></fa-icon> Send to AI
                    }
                </button>
            </div>
        </div>
    </div>
}

@if (currentStep === 3) {
    <!-- STEP 3: Confirm Form (also used for editing existing records) -->
    <form name="form" #form="ngForm">
        <div class="modal-body review-form">
            <!-- Employee & Status -->
            <div class="form-section-title">Employee Information</div>
            <div class="row mb-3">
                <div class="form-group" [ngClass]="isAdding ? 'col-md-8' : 'col-md-6'">
                    <label for="hrRef">Employee</label>
                    @if (employeeMatchMessage && target.hrRef === 0) {
                        <div
                            class="small mb-1"
                            [class.text-success]="topMatchConfidence === 'high'"
                            [class.text-warning]="topMatchConfidence !== 'high'"
                        >
                            {{ employeeMatchMessage }}
                        </div>
                    }
                    <select class="form-select" [(ngModel)]="target.hrRef" name="hrRef" id="hrRef" (ngModelChange)="onEmployeeSelected()">
                        <option [ngValue]="0">-- Select Employee --</option>
                        @for (emp of parentComponent.employees; track emp.hrRef) {
                            <option [ngValue]="emp.hrRef">{{ emp.firstName }} {{ emp.lastName }} ({{ emp.hrRef }})</option>
                        }
                    </select>
                </div>
                <div class="form-group" [ngClass]="isAdding ? 'col-md-4' : 'col-md-3'">
                    <label for="jobTitle">Job Title</label>
                    <input class="form-control" id="jobTitle" [value]="jobTitle" readonly />
                </div>
                @if (!isAdding) {
                    <div class="col-md-3 form-group">
                        <label for="status">Status</label>
                        <select class="form-select" [(ngModel)]="target.status" name="status" id="status">
                            <option value="Draft">Draft</option>
                            <option value="Processed">Processed</option>
                            <option value="Approved">Approved</option>
                        </select>
                    </div>
                }
            </div>

            <!-- Incident Details -->
            <div class="form-section-title">Incident Details</div>
            <div class="row mb-3">
                <div class="col-md-4 form-group">
                    <label for="date">Incident Date</label>
                    <input type="date" class="form-control" [(ngModel)]="dateString" name="date" id="date" />
                </div>
                <div class="col-md-4 form-group">
                    <label for="code">Code (HRCM Type N)</label>
                    <select class="form-select" [(ngModel)]="target.code" name="code" id="code">
                        <option value="">-- Select --</option>
                        @for (c of parentComponent.lookups.hrcmCodes; track c.code) {
                            <option [value]="c.code">{{ c.code }} - {{ c.description }}</option>
                        }
                    </select>
                </div>
                <div class="col-md-4 form-group">
                    <label for="udProgressiveDiscipline">Progressive Discipline</label>
                    <select
                        class="form-select"
                        [(ngModel)]="target.udProgressiveDiscipline"
                        name="udProgressiveDiscipline"
                        id="udProgressiveDiscipline"
                    >
                        <option value="">-- Select --</option>
                        <option value="Verbal Warning">Verbal Warning</option>
                        <option value="Written Warning">Written Warning</option>
                        <option value="Final Warning">Final Warning</option>
                        <option value="Suspension">Suspension</option>
                        <option value="Termination">Termination</option>
                    </select>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-12 form-group">
                    <label for="incidentDesc">Incident Description</label>
                    <textarea
                        class="form-control"
                        [(ngModel)]="target.incidentDesc"
                        name="incidentDesc"
                        id="incidentDesc"
                        rows="3"
                    ></textarea>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-12 form-group">
                    <label for="discAction">Disciplinary Action Taken</label>
                    <textarea class="form-control" [(ngModel)]="target.discAction" name="discAction" id="discAction" rows="3"></textarea>
                </div>
            </div>

            <!-- Action & Follow-up -->
            <div class="form-section-title">Action & Follow-up</div>
            <div class="row mb-3">
                <div class="col-md-4 form-group">
                    <label for="daysSuspended">Days Suspended</label>
                    <input
                        type="number"
                        class="form-control"
                        [(ngModel)]="target.daysSuspended"
                        name="daysSuspended"
                        id="daysSuspended"
                        min="0"
                    />
                </div>
                <div class="col-md-4 form-group">
                    <label for="warningNoticeYn">Warning Notice</label>
                    <select class="form-select" [(ngModel)]="target.warningNoticeYn" name="warningNoticeYn" id="warningNoticeYn">
                        <option value="">-- Select --</option>
                        <option value="Y">Yes</option>
                        <option value="N">No</option>
                    </select>
                </div>
                <div class="col-md-4 form-group">
                    <label for="followUpDate">Follow-Up Date</label>
                    <input type="date" class="form-control" [(ngModel)]="followUpDateString" name="followUpDate" id="followUpDate" />
                </div>
            </div>

            <!-- Comments -->
            <div class="form-section-title">Comments</div>
            <div class="row mb-3">
                <div class="col-md-6 form-group">
                    <label for="udEmployeeComments">Employee Comments</label>
                    <textarea
                        class="form-control"
                        [(ngModel)]="target.udEmployeeComments"
                        name="udEmployeeComments"
                        id="udEmployeeComments"
                        rows="3"
                    ></textarea>
                </div>
                <div class="col-md-6 form-group">
                    <label for="udSupervisorComments">Supervisor Comments</label>
                    <textarea
                        class="form-control"
                        [(ngModel)]="target.udSupervisorComments"
                        name="udSupervisorComments"
                        id="udSupervisorComments"
                        rows="3"
                    ></textarea>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-12 form-group">
                    <label for="notes">Additional Notes</label>
                    <textarea class="form-control" [(ngModel)]="target.notes" name="notes" id="notes" rows="2"></textarea>
                </div>
            </div>

            @if (target.rawTranscription) {
                <details class="mb-3">
                    <summary class="text-muted" style="cursor: pointer; font-size: 13px">View Raw Transcription</summary>
                    <div
                        class="form-control mt-2"
                        style="min-height: 60px; background: var(--bs-body-bg); white-space: pre-wrap; border-radius: 10px"
                    >
                        {{ target.rawTranscription }}
                    </div>
                </details>
            }
        </div>
        <div class="modal-footer">
            <div class="wizard-footer">
                <div>
                    @if (isAdding) {
                        <button class="btn btn-outline-secondary" (click)="goToStep(2)">
                            <fa-icon [icon]="arrowLeftIcon" class="me-1"></fa-icon> Back to Review
                        </button>
                    }
                </div>
                <div class="wizard-footer-end">
                    <button id="btnSave" type="submit" class="btn btn-primary" (click)="save()" [disabled]="form.invalid || isSaving">
                        @if (isSaving) {
                            <span><fa-icon [icon]="faSpinner" [animation]="'spin'"></fa-icon>&nbsp;Saving</span>
                        } @else {
                            <span>Save</span>
                        }
                    </button>
                    <button id="btnCancel" type="button" class="btn btn-secondary" (click)="cancel()" [disabled]="isSaving">Cancel</button>
                </div>
            </div>
        </div>
    </form>
}
